<template>
  <div>
    <v-card flat>
      <v-card-text>
        <div class="d-flex align-center mb-4">
          <v-icon class="mr-2" color="primary">mdi-cog</v-icon>
          <span class="text-h5">Settings</span>
        </div>

        <v-divider class="mb-6"></v-divider>

        <v-skeleton-loader
          v-if="initialLoading"
          type="article, article, article"
          class="mb-4"
        ></v-skeleton-loader>

        <div v-else-if="settings.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey-lighten-1" class="mb-4"
            >mdi-information-outline</v-icon
          >
          <p class="text-body-1 text-grey">No settings available</p>
        </div>

        <v-form v-else ref="form">
          <v-expansion-panels multiple variant="accordion">
            <v-expansion-panel
              v-for="(setting, index) in settings"
              :key="index"
            >
              <v-expansion-panel-title>
                <div class="d-flex align-center justify-space-between w-100">
                  <span class="font-weight-medium">{{ setting.name }}</span>
                  <v-chip size="small" color="primary" variant="tonal">
                    National
                  </v-chip>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- National -->
                <v-text-field
                  v-model="setting.national"
                  label="National Value"
                  density="compact"
                  variant="outlined"
                  class="mb-4"
                  @paste="handlePaste"
                  @keypress="handleKeyPress($event, setting.national)"
                  :rules="numberRules"
                />

                <v-divider class="mb-4" />
                <div class="text-caption text-grey mb-2">State-wise values</div>
                <v-text-field
                  v-model="setting.stateSearch"
                  label="Search state"
                  prepend-inner-icon="mdi-magnify"
                  density="compact"
                  variant="outlined"
                  clearable
                  class="mb-3"
                />
                <!-- State-wise values -->
                <v-row>
                  <v-col
                    v-for="state in filteredStates(setting)"
                    :key="state.isoCode"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-text-field
                      v-model="state.value"
                      :label="state.stateName"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      @paste="handlePaste"
                      @keypress="handleKeyPress($event, state.value)"
                      :rules="numberRules"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="d-flex justify-end mt-6">
            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-content-save"
              @click="saveSettings"
              :disabled="settingsLoading"
              :loading="settingsLoading"
              size="large"
            >
              Save Settings
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import USAStates from "@/JsonFiles/USAStatesCities.json";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { edit_master_medications } from "@/graphql/mutations";
import GetMedications from "@/mixins/Medication/GetMedications";
export default {
  name: "SettingsCard",
  mixins: [GetMedications],
  components: {
    SnackbarComponent,
  },
  data() {
    return {
      settings: [],
      numberRules: [
        (v) => v === "" || /^(\d+(\.\d+)?)$/.test(v) || "Enter a valid number",
      ],

      settingsLoading: false,
      initialLoading: true,

      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  async mounted() {
    this.loadSettings();
  },
  methods: {
    filteredStates(setting) {
      if (!setting.stateSearch) return setting.states;

      const search = setting.stateSearch.trim().toLowerCase();

      return setting.states.filter((state) => {
        return (
          state.stateName.toLowerCase().includes(search) || // "Texas"
          state.isoCode.toLowerCase().includes(search) // "TX"
        );
      });
    },

    onTabActivated() {
      this.loadSettings();
    },
    async loadSettings() {
      this.initialLoading = true;
      try {
        await this.fetchMedications("MASTER_SETTINGS");
        this.fetchSettings();
      } catch (error) {
        console.error("Error loading settings:", error);
        this.snackbar = {
          show: true,
          message: "Failed to load settings",
          color: "error",
        };
      } finally {
        this.initialLoading = false;
      }
    },
    handleKeyPress(event, currentValue) {
      const char = event.key;
      const value = currentValue || "";

      // Allow digits
      if (/[0-9]/.test(char)) return true;

      // Allow '.' only once
      if (char === "." && !value.includes(".")) return true;

      // Allow backspace, delete, tab, escape, enter
      if (["Backspace", "Delete", "Tab", "Escape", "Enter"].includes(char)) {
        return true;
      }

      // Allow arrow keys
      if (char.startsWith("Arrow")) return true;

      event.preventDefault();
      return false;
    },
    fetchSettings() {
      if (!this.medicationsList.length) {
        this.settings = [];
        return;
      }

      const backendSettings =
        this.medicationsList[0]?.utilization_configurations || [];

      this.settings = backendSettings.map((setting) => {
        // Parse old state data if exists
        let stateMap = {};
        try {
          stateMap =
            typeof setting.states === "string"
              ? JSON.parse(setting.states)
              : setting.states || {};
        } catch {
          stateMap = {};
        }

        return {
          name: setting.name,
          national: setting.national === "N/A" ? "" : setting.national,
          stateSearch: "", // ✅ REQUIRED
          states: USAStates.states.map((s) => ({
            isoCode: s.isoCode,
            stateName: s.name,
            value: stateMap?.[s.name] || "",
          })),
        };
      });
    },
    handlePaste(e) {
      const text = e.clipboardData.getData("text");
      if (!/^(\d+(\.\d+)?)$/.test(text)) {
        e.preventDefault();
      }
    },

    async saveSettings() {
      if (!this.$refs.form) return;

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        this.snackbar = {
          show: true,
          message: "Please fix validation errors before saving",
          color: "warning",
        };
        return;
      }

      this.settingsLoading = true;
      try {
        const settings = this.settings.map((setting) => ({
          name: setting.name,
          national: setting.national || "N/A",
        states: setting.states.reduce((acc, s) => {
  if (s.value) acc[s.stateName] = s.value;
  return acc;
}, {}),
        }));

        const input = {
          medication_master_id:
            this.medicationsList[0]?.medication_master_id ||
            this.medicationsList[0]?.id,
          utilization_configurations: JSON.stringify(settings),
        };

        const response = await client.graphql({
          query: edit_master_medications,
          variables: { input: input },
        });

        const responseData = JSON.parse(response.data.edit_master_medications);

        if (responseData.status == "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Settings saved successfully",
            color: "success",
          };
          await this.fetchMedications("MASTER_SETTINGS");
          this.fetchSettings();
        } else {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to save settings",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error saving settings:", error);
        this.snackbar = {
          show: true,
          message:
            error.errors?.[0]?.message ||
            "An error occurred while saving settings",
          color: "error",
        };
      } finally {
        this.settingsLoading = false;
      }
    },
  },
};
</script>

<style>
</style>